import { makeAutoObservable, runInAction } from 'mobx'
import agent from '../api/agent'
import { Part, PartFormValues } from '../models/part'

export default class PartStore {
  partsRegistry = new Map<string, Part>()
  pendingPartsRegistry = new Map<string, Part>()
  loading = false
  loadingInitial = false
  loadingPending = false

  constructor () {
    makeAutoObservable(this)
  }

  get partsByDate () {
    return Array.from(this.partsRegistry.values()).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  }

  get pendingPartsByDate () {
    return Array.from(this.pendingPartsRegistry.values()).sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    )
  }

  loadParts = async (params?: { tagIds?: string[]; tagMatch?: string }) => {
    this.loadingInitial = true
    try {
      const result = await agent.Parts.list({
        tagIds: params?.tagIds,
        tagMatch: params?.tagMatch ?? 'any'
      })
      runInAction(() => {
        this.partsRegistry.clear()
        result.items.forEach((p) => this.partsRegistry.set(p.id, p))
        this.loadingInitial = false
      })
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loadingInitial = false))
    }
  }

  loadPendingParts = async () => {
    this.loadingPending = true
    try {
      const parts = await agent.Parts.listPending()
      runInAction(() => {
        this.pendingPartsRegistry.clear()
        parts.forEach((p) => this.pendingPartsRegistry.set(p.id, p))
        this.loadingPending = false
      })
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loadingPending = false))
    }
  }

  approvePart = async (id: string) => {
    this.loading = true
    try {
      await agent.Parts.approve(id)
      runInAction(() => {
        this.pendingPartsRegistry.delete(id)
        this.loading = false
      })
      return true
    } catch (error) {
      runInAction(() => (this.loading = false))
      throw error
    }
  }

  rejectPart = async (id: string, reason: string) => {
    this.loading = true
    try {
      await agent.Parts.reject(id, reason)
      runInAction(() => {
        this.pendingPartsRegistry.delete(id)
        this.loading = false
      })
      return true
    } catch (error) {
      runInAction(() => (this.loading = false))
      throw error
    }
  }

  createPart = async (part: PartFormValues) => {
    this.loading = true
    try {
      const result = await agent.Parts.create(part)
      runInAction(() => (this.loading = false))
      return result?.id
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loading = false))
    }
  }

  updatePart = async (id: string, part: PartFormValues) => {
    this.loading = true
    try {
      const updated = await agent.Parts.update(id, part)
      runInAction(() => {
        if (updated) this.partsRegistry.set(updated.id, updated)
        this.loading = false
      })
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loading = false))
      throw error
    }
  }

  uploadPartImage = async (partId: string, file: File, consent?: { accepted: boolean; version: number }) => {
    this.loading = true
    try {
      const updated = await agent.Parts.uploadImage(partId, file, consent)
      runInAction(() => {
        if (updated) this.partsRegistry.set(updated.id, updated)
        this.loading = false
      })
      return updated
    } catch (error) {
      runInAction(() => (this.loading = false))
      throw error
    }
  }

  deletePart = async (id: string) => {
    this.loading = true
    try {
      await agent.Parts.delete(id)
      runInAction(() => {
        this.partsRegistry.delete(id)
        this.pendingPartsRegistry.delete(id)
        this.loading = false
      })
    } catch (error) {
      runInAction(() => (this.loading = false))
      throw error
    }
  }
}
